import React, { useEffect } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, CRUDActionEnum, StatusStateEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import toastsActionCreators from "~/store/toasts/actions";
import { Alert } from "~/components-shared";
import useAbacExt from "~/hooks/abac";
import manageCompanySelectors from "~/store/manageCompanies/selectors";
import manageCompaniesActionCreators from "~/store/manageCompanies/actions";
import ManageCompanyForm from "~/forms/ManageCompanyForm";
import manageCompanySchema from "~/forms/ManageCompanyForm/manageCompanySchema";
const Sticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const ManageCompanyUpdateModal = () => {
  const {
    t
  } = useTranslation();
  const company = useSelector(manageCompanySelectors.getSelected);
  const companyId = company?.id || "";
  const status = useSelector(manageCompanySelectors.getStatusByCompany(companyId));
  const error = useSelector(manageCompanySelectors.getErrorByCompany(companyId));
  const dispatch = useDispatch();
  const {
    userHasOneOf
  } = useAbacExt();
  const actions = [];
  const defaultValues = {
    name: company?.name || "",
    description: company?.description || "",
    context: company?.context || "",
    url: company?.url || "",
    employeeCount: company?.employeeCount || 0,
    logo: company?.logo || "",
    emailDomains: company?.emailDomains || "",
    settings: {
      isPaidPlan: company?.settings.isPaidPlan || false,
      recommendedIntegrations: company?.settings.recommendedIntegrations || [],
      preferredIntegrations: company?.settings.preferredIntegrations,
      sendSlackStatusReport: company?.settings.sendSlackStatusReport,
      memberBlacklist: company?.settings.memberBlacklist,
      costPerVolume: company?.settings.costPerVolume || {},
      costAiAssistantCap: company?.settings.costAiAssistantCap || 0,
      costUsageCap: company?.settings.costUsageCap || 0,
      hideSlackPrivateMessages: company?.settings.hideSlackPrivateMessages !== undefined ? company?.settings.hideSlackPrivateMessages : false,
      privateSlackChannelApprovals: company?.settings.privateSlackChannelApprovals !== undefined ? company?.settings.privateSlackChannelApprovals : true,
      eventBlacklist: company?.settings.eventBlacklist,
      ssoAuthentication: company?.settings.ssoAuthentication !== undefined ? company?.settings.ssoAuthentication : false,
      topicEventDirectMapping: company?.settings.topicEventDirectMapping,
      detectionThresholds: company?.settings.detectionThresholds,
      excludedSalesforceUsers: company?.settings.excludedSalesforceUsers,
      excludedSalesforceEmails: company?.settings.excludedSalesforceEmails,
      salesforceCustomFields: company?.settings.salesforceCustomFields,
      salesforceStageMapping: company?.settings.salesforceStageMapping,
      statusMapping: company?.settings.statusMapping
    },
    authorities: company?.authorities || [],
    weight: company?.weight || 0
  };
  const {
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(manageCompanySchema),
    defaultValues
  });
  useEffect(() => {
    if (company) {
      reset(defaultValues);
    }
  }, [companyId]);
  const onSubmit = handleSubmit(data => {
    if (companyId) {
      dispatch(manageCompaniesActionCreators.updateStarted(companyId, data, _ref => {
        let {
          data: newCompany
        } = _ref;
        if (newCompany) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("ManageCompanyUpdateModal.companyUpdated", {
              name: newCompany.name
            }),
            timeout: 10000
          }));
        }
      }));
    }
  });
  const handleCancel = () => {
    dispatch(manageCompaniesActionCreators.actionChanged(null, null));
  };
  const handleDelete = () => {
    dispatch(manageCompaniesActionCreators.actionChanged(CRUDActionEnum.DELETE, companyId));
  };
  if (userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE])) {
    actions.push({
      icon: "bin",
      tip: t("common.actions.delete"),
      onClick: handleDelete
    });
  }
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("ManageCompanyUpdateModal.updateCompany"),
    onClose: handleCancel,
    fullWidth: true,
    actions: actions,
    maxWidth: "1200px"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Sticky, null, /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error)), /*#__PURE__*/React.createElement(ManageCompanyForm, {
    company: company,
    control: control,
    watch: watch,
    setValue: setValue,
    setError: setError
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("ManageCompanyUpdateModal.saveCompany")))));
};
export default ManageCompanyUpdateModal;